import { RefObject, useEffect, useRef } from "react";

import { useHotkeys } from "react-hotkeys-hook";

export interface UseDismissOptions {
  /**
   * Refs to exclude from click outside detection (e.g., dropdowns, tooltips)
   */
  excludeRefs?: RefObject<HTMLElement>[];
  /**
   * Whether to enable ESC key support
   * @default true
   */
  enableEscape?: boolean;
  /**
   * Whether to enable click outside detection
   * @default true
   */
  enableClickOutside?: boolean;
  /**
   * Whether the hook is enabled
   * @default true
   */
  enabled?: boolean;
  /**
   * The function to call when the dismiss is triggered
   */
  onDismiss?: () => void;
}

export const useDismiss = <T extends HTMLElement = HTMLElement>(
  handlerOrOptions: (() => void) | UseDismissOptions,
  options?: UseDismissOptions,
) => {
  // Support both patterns: useDismiss(handler, options) and useDismiss({ onDismiss, ... })
  const isOptionsOnly = typeof handlerOrOptions !== "function";
  const handler = isOptionsOnly ? undefined : handlerOrOptions;
  const finalOptions = isOptionsOnly ? handlerOrOptions : options;

  const {
    excludeRefs,
    enableEscape = true,
    enableClickOutside = true,
    enabled = true,
    onDismiss,
  } = finalOptions || {};
  const ref = useRef<T>(null);

  // Use onDismiss from options if provided, otherwise fall back to handler parameter
  const dismissHandler = onDismiss ?? handler;
  const handlerRef = useRef(dismissHandler);
  const excludeRefsRef = useRef(excludeRefs);

  // Update refs when they change
  useEffect(() => {
    handlerRef.current = dismissHandler;
    excludeRefsRef.current = excludeRefs;
  }, [dismissHandler, excludeRefs]);

  useEffect(() => {
    if (!enabled || !enableClickOutside) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      // Check if click is inside the main ref
      if (ref.current && ref.current.contains(target)) {
        return;
      }

      // Check if click is inside any excluded refs
      if (excludeRefsRef.current) {
        for (const excludeRef of excludeRefsRef.current) {
          if (excludeRef.current && excludeRef.current.contains(target)) {
            return;
          }
        }
      }

      // Click is outside all refs, call handler
      if (handlerRef.current) {
        handlerRef.current();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [enabled, enableClickOutside]);

  // Handle ESC key
  useHotkeys(
    "esc",
    () => {
      if (enabled && handlerRef.current) {
        handlerRef.current();
      }
    },
    { enabled: enabled && enableEscape },
    [enabled, enableEscape],
  );

  return ref;
};
